import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Progress } from 'antd';
import { 
  DashboardOutlined, 
  DatabaseOutlined, 
  HddOutlined, 
  SwapOutlined, 
  CheckCircleOutlined, 
  SyncOutlined 
} from '@ant-design/icons';
import { fetchSystemUsage } from '../../../Service/Dashboard_API/dashboardApi';

const RealTimeStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const getStats = async (silent = false) => {
      if (!silent) setLoading(true);
      setIsUpdating(true);
      try {
        const data = await fetchSystemUsage();
        if (isMounted) {
          setStats(data);
          setError(false);
        }
      } catch (err) {
        if (isMounted) {
          console.error('Error fetching system stats:', err);
          setError(true);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
          setIsUpdating(false);
        }
      }
    };

    getStats();
    const interval = setInterval(() => {
      getStats(true);
    }, 5000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  const formatStorage = (gb) => {
    if (gb === undefined || gb === null) return '0 GB';
    return gb >= 1000 ? `${(gb / 1024).toFixed(1)} TB` : `${gb} GB`;
  };

  const getStatusColor = (percent) => {
    if (percent >= 85) return '#ef4444'; // Red
    if (percent >= 70) return '#eab308'; // Yellow
    return '#10b981'; // Green
  };

  if (loading) {
    return (
      <Card style={{ border: '1px solid var(--border-color, rgba(0,0,0,0.08))', borderRadius: 12, background: 'var(--panel-color, #ffffff)' }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100, color: 'var(--text-secondary)' }}>
          <SyncOutlined spin style={{ marginRight: 8, fontSize: 18 }} />
          <span>Connecting to Server Metrics...</span>
        </div>
      </Card>
    );
  }

  // If there's an error and no previous stats, show error view
  if (error && !stats) {
    return (
      <Card style={{ background: 'var(--panel-color, #ffffff)', border: '1px solid rgba(239, 68, 68, 0.2)', borderRadius: 12 }}>
        <div style={{ color: '#ef4444', textAlign: 'center', padding: 12 }}>
          Failed to fetch system usage stats. Please verify server connection.
        </div>
      </Card>
    );
  }

  const { cpu, memory, storage, network } = stats || {
    cpu: 0,
    memory: { total: 0, used: 0, percent: 0 },
    storage: { total: 0, used: 0, percent: 0 },
    network: { rx: 0, tx: 0 }
  };

  return (
    <Card
      style={{
        borderRadius: 16,
        marginBottom: 24,
        background: 'var(--panel-color, #ffffff)',
        borderColor: 'var(--border-color, rgba(0,0,0,0.08))'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: error ? '#ef4444' : '#10b981',
            marginRight: 8,
            boxShadow: error ? '0 0 8px #ef4444' : '0 0 8px #10b981',
            animation: error ? 'none' : 'pulse 2s infinite'
          }} />
          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)' }}>
            Server Health Status: <strong style={{ color: error ? '#ef4444' : '#10b981' }}>{error ? 'Degraded' : 'Operational'}</strong>
          </span>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center' }}>
          {isUpdating ? <SyncOutlined spin style={{ marginRight: 4 }} /> : <CheckCircleOutlined style={{ marginRight: 4, color: '#10b981' }} />}
          <span>Metrics poll auto updates (5s)</span>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7); }
          70% { transform: scale(1); box-shadow: 0 0 0 6px rgba(16, 185, 129, 0); }
          100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
        }
        .stat-card-glass {
          background: #f8fafc !important;
          border: 1px solid var(--border-color, #e2e8f0) !important;
          border-radius: 12px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          color: var(--text-color, #1e293b);
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
        }
        .stat-card-glass:hover {
          transform: translateY(-3px);
          border-color: rgba(59, 130, 246, 0.4) !important;
          box-shadow: 0 8px 16px -4px rgba(0, 0, 0, 0.08);
        }
        [data-theme='dark'] .stat-card-glass {
          background: rgba(30, 41, 59, 0.45) !important;
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid var(--border-color, rgba(255, 255, 255, 0.06)) !important;
          color: #fff;
          box-shadow: none;
        }
        [data-theme='dark'] .stat-card-glass:hover {
          border-color: var(--border-color) !important;
          box-shadow: 0 12px 24px -10px rgba(0, 0, 0, 0.5);
        }
        .stat-card-glass .ant-progress-inner {
          background-color: rgba(0, 0, 0, 0.06) !important;
        }
        [data-theme='dark'] .stat-card-glass .ant-progress-inner {
          background-color: rgba(255, 255, 255, 0.08) !important;
        }
        .stat-card-track {
          height: 6px;
          width: 100%;
          background: rgba(0, 0, 0, 0.06);
          border-radius: 100px;
          margin-top: 22px;
        }
        [data-theme='dark'] .stat-card-track {
          background: rgba(255, 255, 255, 0.08);
        }
        .stat-card-subtext {
          display: flex;
          justify-content: space-between;
          margin-top: 8px;
          font-size: 11px;
          color: var(--text-secondary);
          font-weight: 500;
        }
      `}</style>

      <Row gutter={[16, 16]}>
        {/* CPU Usage Card */}
        <Col xs={24} sm={12} lg={6}>
          <Card className="stat-card-glass" bodyStyle={{ padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>CPU Load</span>
                <h3 style={{ fontSize: 24, fontWeight: 700, margin: '4px 0', color: 'var(--text-color)' }}>{cpu}%</h3>
              </div>
              <div style={{ 
                background: 'rgba(59, 130, 246, 0.15)', 
                padding: 8, 
                borderRadius: 8, 
                color: '#3b82f6',
                display: 'flex', 
                alignItems: 'center' 
              }}>
                <DashboardOutlined style={{ fontSize: 20 }} />
              </div>
            </div>
            <Progress 
              percent={cpu} 
              showInfo={false} 
              strokeColor={getStatusColor(cpu)} 
              style={{ marginTop: 16 }}
            />
            <div className="stat-card-subtext">
              <span>Load: Active</span>
              <span>100% Max</span>
            </div>
          </Card>
        </Col>

        {/* Memory Usage Card */}
        <Col xs={24} sm={12} lg={6}>
          <Card className="stat-card-glass" bodyStyle={{ padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>RAM Allocation</span>
                <h3 style={{ fontSize: 24, fontWeight: 700, margin: '4px 0', color: 'var(--text-color)' }}>{memory.percent}%</h3>
              </div>
              <div style={{ 
                background: 'rgba(168, 85, 247, 0.15)', 
                padding: 8, 
                borderRadius: 8, 
                color: '#a855f7',
                display: 'flex', 
                alignItems: 'center' 
              }}>
                <DatabaseOutlined style={{ fontSize: 20 }} />
              </div>
            </div>
            <Progress 
              percent={memory.percent} 
              showInfo={false} 
              strokeColor={getStatusColor(memory.percent)} 
              style={{ marginTop: 16 }}
            />
            <div className="stat-card-subtext">
              <span>{memory.used} GB Used</span>
              <span>{memory.total} GB Total</span>
            </div>
          </Card>
        </Col>

        {/* Storage Capacity Card */}
        <Col xs={24} sm={12} lg={6}>
          <Card className="stat-card-glass" bodyStyle={{ padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Disk Storage</span>
                <h3 style={{ fontSize: 24, fontWeight: 700, margin: '4px 0', color: 'var(--text-color)' }}>{storage.percent}%</h3>
              </div>
              <div style={{ 
                background: 'rgba(236, 72, 153, 0.15)', 
                padding: 8, 
                borderRadius: 8, 
                color: '#ec4899',
                display: 'flex', 
                alignItems: 'center' 
              }}>
                <HddOutlined style={{ fontSize: 20 }} />
              </div>
            </div>
            <Progress 
              percent={storage.percent} 
              showInfo={false} 
              strokeColor={getStatusColor(storage.percent)} 
              style={{ marginTop: 16 }}
            />
            <div className="stat-card-subtext">
              <span>{formatStorage(storage.used)} Used</span>
              <span>{formatStorage(storage.total)} HDD</span>
            </div>
          </Card>
        </Col>

        {/* Network Throughput Card */}
        <Col xs={24} sm={12} lg={6}>
          <Card className="stat-card-glass" bodyStyle={{ padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>I/O Network Bandwidth</span>
                <h3 style={{ fontSize: 24, fontWeight: 700, margin: '4px 0', color: 'var(--text-color)' }}>
                  {network.rx} <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>Mbps</span>
                </h3>
              </div>
              <div style={{ 
                background: 'rgba(16, 185, 129, 0.15)', 
                padding: 8, 
                borderRadius: 8, 
                color: '#10b981',
                display: 'flex', 
                alignItems: 'center' 
              }}>
                <SwapOutlined style={{ fontSize: 20 }} />
              </div>
            </div>
            <div className="stat-card-track" />
            <div className="stat-card-subtext">
              <span>▼ RX Rate: {network.rx} Mbps</span>
              <span>▲ TX Rate: {network.tx} Mbps</span>
            </div>
          </Card>
        </Col>
      </Row>
    </Card>
  );
};

export default RealTimeStats;
